import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';

import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import Authors from 'components/Authors';

import useDialog from 'hooks/useDialog';

import { toUserProfileLink } from 'lib/links';
import { capitalizeEveryWord } from 'lib/string';
import { isGenieUser } from 'lib/user';

const AuthorsListDialog = dynamic(() => import('./AuthorsListDialog'), {
  ssr: false,
});

const UserSpecialty = dynamic(() => import('components/UserSpecialty'), {
  ssr: false,
});

const MIN_USERS_TO_SHOW_AUTHORS = 1;

const toFormattedUsersCountTextLink = (authorCount) => {
  const authorCountRemovingMainAuthor = authorCount - MIN_USERS_TO_SHOW_AUTHORS;

  return authorCountRemovingMainAuthor === MIN_USERS_TO_SHOW_AUTHORS
    ? `${authorCountRemovingMainAuthor} usuário`
    : `${authorCountRemovingMainAuthor} usuários`;
};

const OfferAuthors = ({
  authorCount,
  authors,
  className,
  offerId,
  userId,
  userName,
  userSpecialtyName,
  userUsername,
}) => {
  const { showDialog } = useDialog();

  const onClick = () => {
    showDialog(AuthorsListDialog, { offerId });
  };

  return (
    <div className={twMerge('mt-2 flex items-center gap-3', className)}>
      <Authors
        authors={authors}
        className={authorCount <= MIN_USERS_TO_SHOW_AUTHORS && 'cursor-default'}
        firstAuthorId={userId}
        size="size6"
        onClick={() => {
          if (authorCount <= MIN_USERS_TO_SHOW_AUTHORS) {
            return;
          }

          onClick();
        }}
      />
      <div className="flex flex-col gap-1">
        <Text
          as="span"
          className="line-clamp-2 text-neutral-low-300 dark:text-neutral-high-300"
          size="size3"
        >
          Oferta postada por{' '}
          {isGenieUser(userId) ? (
            <Text
              className="flex whitespace-nowrap text-rd-primary-700 dark:text-rd-primary-700"
              size="size3"
              bold
            >
              {capitalizeEveryWord(userName)}
            </Text>
          ) : (
            <NextLink
              href={toUserProfileLink(userUsername)}
              prefetch={false}
              passHref
            >
              <Link
                className="text-rd-primary-700 dark:text-rd-primary-700"
                size="size3"
                bold
              >
                {capitalizeEveryWord(userName)}
              </Link>
            </NextLink>
          )}
          {authorCount > MIN_USERS_TO_SHOW_AUTHORS && (
            <>
              {' e sugerida por mais '}
              <Link
                className="text-rd-primary-700 dark:text-rd-primary-700"
                size="size3"
                bold
                onClick={onClick}
              >
                {toFormattedUsersCountTextLink(authorCount)}
              </Link>
            </>
          )}
        </Text>
        {userSpecialtyName && (
          <UserSpecialty
            size="size3"
            userId={userId}
            userSpecialtyName={userSpecialtyName}
          />
        )}
      </div>
    </div>
  );
};

OfferAuthors.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      photo: PropTypes.string.isRequired,
      typeName: PropTypes.string.isRequired,
      specialityName: PropTypes.string,
      level: PropTypes.number.isRequired,
      link: PropTypes.string.isRequired,
    })
  ).isRequired,
  authorCount: PropTypes.number.isRequired,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  offerId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  userName: PropTypes.string.isRequired,
  userSpecialtyName: PropTypes.string,
  userUsername: PropTypes.string.isRequired,
};

export default OfferAuthors;
